import { LazyRouteFunction, RouteObject } from 'react-router-dom';
import { ChunkLoadFailurePanel } from '@components/ChunkLoadError';
import { isChunkLoadError } from '@lib/chunkLoad';

type LazyRoute = Awaited<ReturnType<LazyRouteFunction<RouteObject>>>;

/**
 * Resolves a dynamically imported module into a route component.
 *
 * When the chunk itself cannot be loaded (typically a stale tab after a
 * deploy, or a network problem) the route renders an inline chunk error view
 * in place of the page, rather than rejecting. A rejection would reach the
 * router's `errorElement` on the root route and replace the whole layout,
 * including the nav bar and call bar. Any other error is rethrown unchanged.
 */
export async function lazyComponent(
  importFunction: Promise<any>,
  field = 'default',
): Promise<LazyRoute> {
  try {
    const importResult = await importFunction;

    return { Component: importResult[field] };
  } catch (error) {
    if (!isChunkLoadError(error)) {
      throw error;
    }

    return { Component: () => <ChunkLoadFailurePanel error={error} inline /> };
  }
}

export async function lazyWrapComponent(
  lazyRoutePromise: Promise<LazyRoute>,
  Component: (props: { children: React.ReactNode }) => React.ReactNode,
): Promise<LazyRoute> {
  const lazyRoute = await lazyRoutePromise;
  const RouteComponent = lazyRoute.Component;
  if (RouteComponent) {
    lazyRoute.Component = (props) => (
      <Component>
        <RouteComponent {...props} />
      </Component>
    );
  }
  return lazyRoute;
}
